import React, {
    useEffect,
    useImperativeHandle,
    useState,
    forwardRef,
} from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import {
    useStripe,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    AddressElement,
    useElements,
} from '@stripe/react-stripe-js'
import './BillingComponent.css'
import { UserWalletApi } from '../../apis/UserWallet'
import { useAuth } from '../../auth/Auth'

// Stripe iframe-internal text styling — works alongside the Elements
// appearance theme set in EditBilling.js
const elementOptions = {
    style: {
        base: {
            fontFamily:
                "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
            fontWeight: '400',
            fontSize: '14px',
            color: '#0B2926',
            '::placeholder': { color: '#6B7673' },
        },
        invalid: { color: '#B42222', iconColor: '#B42222' },
    },
}

const fieldLabel = {
    display: 'block',
    font: 'var(--gp-caption)',
    color: 'var(--gp-ink-mute)',
    marginBottom: 6,
}
const fieldWrap = {
    background: 'var(--gp-card)',
    border: '1px solid var(--gp-hairline)',
    borderRadius: 'var(--gp-r-md)',
    padding: '11px 14px',
    transition: 'border-color 0.15s ease',
}

const BillingComponent = forwardRef((props, ref) => {
    const [init, setInit] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [addressComplete, setAddressComplete] = useState(false)
    const [cardNumberComplete, setCardNumberComplete] = useState(false)
    const [cardExpiryComplete, setCardExpiryComplete] = useState(false)
    const [cardCvcComplete, setCardCvcComplete] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [showStripeError, setShowStripeError] = useState(false)
    const [billingAddress, setBillingAddress] = useState({})

    const { isConsumerGuest } = useAuth()
    const stripe = useStripe()
    const elements = useElements()

    useImperativeHandle(ref, () => ({
        handleSubmit,
    }))

    const cardComplete =
        cardNumberComplete && cardExpiryComplete && cardCvcComplete

    // Surface readiness to the parent so it can disable its submit button
    // until card + address (where required) are complete.
    useEffect(() => {
        if (props.onReadyChange) {
            const ready = cardComplete && (isConsumerGuest() || addressComplete)
            props.onReadyChange(ready)
        }
    }, [cardComplete, addressComplete])

    const submitStripeAccount = async () => {
        if (!stripe || !elements) {
            return
        }

        const cardNumber = elements.getElement(CardNumberElement)
        const address = elements.getElement(AddressElement)

        const { complete: addrComplete, value: billingDetails } = address
            ? await address.getValue()
            : { complete: false, value: {} }

        if (!cardComplete) {
            cardNumber?.focus()
            setErrorMessage('Please enter your credit card details')
            setIsLoading(false)
            setShowStripeError(true)
            return false
        }

        if (!addrComplete && !isConsumerGuest()) {
            setErrorMessage('Please enter your billing address')
            setIsLoading(false)
            setShowStripeError(true)
            return false
        }

        const { clientSecret } = await UserWalletApi.createClientSecret()

        const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: isConsumerGuest()
                ? { card: cardNumber }
                : { card: cardNumber, billing_details: billingDetails },
        })

        if (error) {
            setErrorMessage(error.message)
            setShowStripeError(true)
            setIsLoading(false)
            return false
        }

        setShowStripeError(false)
        setIsLoading(true)

        const savedPayment = await savePaymentMethod(setupIntent.payment_method)
        if (savedPayment) {
            await props.creditCardAdded(setupIntent.payment_method)
            return true
        }
        return false
    }

    const savePaymentMethod = async (paymentMethodId) => {
        try {
            const response = await UserWalletApi.put(paymentMethodId)
            if (response.hasOwnProperty('code')) {
                setErrorMessage(response.message)
                setShowStripeError(true)
                setIsLoading(false)
                return false
            }
            return true
        } catch (error) {
            setErrorMessage(
                'An error occurred. Please check your details and try again'
            )
            setShowStripeError(true)
            setIsLoading(false)
            return false
        }
    }

    useEffect(() => {
        if (props.setParentLoading) {
            props.setParentLoading(isLoading)
        }
    }, [isLoading])

    const handleSubmit = async () => {
        setErrorMessage('')
        setIsLoading(true)
        return await submitStripeAccount()
    }

    useEffect(() => {
        if (props.isUpdate) {
            setBillingAddress(props.billingAddress)
        }
        setInit(true)
    }, [stripe])

    if (!init) return null

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {isConsumerGuest() && (
                <div
                    style={{
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-ink-mute)',
                        background: 'var(--gp-surface)',
                        border: '1px solid var(--gp-hairline)',
                        borderRadius: 'var(--gp-r-md)',
                        padding: '10px 14px',
                    }}
                >
                    We may place a $0.50 temporary authorization on your card.
                    <br />
                    This verifies your payment method and is released after verification.
                </div>
            )}

            {/* Card number / Expiry / CVC */}
            <div className="bc-card-fields">
                <div>
                    <label style={fieldLabel}>Card number</label>
                    <div style={fieldWrap} data-testid="cypress-billing-card-element">
                        <CardNumberElement
                            options={{
                                ...elementOptions,
                                showIcon: true,
                                placeholder: '1234 1234 1234 1234',
                            }}
                            onChange={(e) => setCardNumberComplete(e.complete)}
                            onReady={(el) => el.focus()}
                        />
                    </div>
                </div>
                <div>
                    <label style={fieldLabel}>Expiry</label>
                    <div style={fieldWrap}>
                        <CardExpiryElement
                            options={{ ...elementOptions, placeholder: 'MM/YY' }}
                            onChange={(e) => setCardExpiryComplete(e.complete)}
                        />
                    </div>
                </div>
                <div>
                    <label style={fieldLabel}>CVC</label>
                    <div style={fieldWrap}>
                        <CardCvcElement
                            options={{ ...elementOptions, placeholder: 'CVC' }}
                            onChange={(e) => setCardCvcComplete(e.complete)}
                        />
                    </div>
                </div>
            </div>

            {/* Billing address — Stripe AddressElement themed via parent appearance */}
            {!isConsumerGuest() && (
                <div data-testid="cypress-billing-address-element">
                    <AddressElement
                        onChange={(e) => setAddressComplete(e.complete)}
                        options={{
                            mode: 'billing',
                            defaultValues: {
                                name: billingAddress?.name,
                                address: {
                                    city: billingAddress?.address?.city,
                                    country: billingAddress?.address?.country,
                                    line1: billingAddress?.address?.line1,
                                    line2: billingAddress?.address?.line2,
                                    postal_code: billingAddress?.address?.postalCode,
                                    state: billingAddress?.address?.state,
                                },
                            },
                        }}
                    />
                </div>
            )}

            {showStripeError && (
                <div
                    style={{
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-danger)',
                        background:
                            'color-mix(in srgb, var(--gp-danger) 8%, #ffffff)',
                        border: '1px solid var(--gp-danger)',
                        borderRadius: 'var(--gp-r-sm)',
                        padding: '8px 12px',
                        textAlign: 'center',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    {errorMessage}
                </div>
            )}

            {isLoading && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-ink-mute)',
                    }}
                >
                    <CircularProgress size={14} color="inherit" />
                    Authorizing…
                </div>
            )}
        </div>
    )
})

export default BillingComponent
